/**
 * Connections Explorer — flagship network navigation for The Archive.
 *
 * Structured view (default): focus card → documented connections →
 * evidence for every edge. Board view: red-string investigation canvas.
 * Path Finder: documented chain between any two entities.
 * Every edge on every surface is backed by verified appearances in
 * published dossiers — nothing speculative is ever drawn.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Network, LayoutList, Workflow, GitBranch, Search, X, Loader2, Users } from "lucide-react";
import { fetchGraph, searchEntities, KIND_META, groupOf } from "../components/network/explorerApi";
import { StructuredView } from "../components/network/StructuredView";
import { BoardView } from "../components/network/BoardView";
import { PortraitWall } from "../components/network/PortraitWall";
import { PathFinder } from "../components/network/PathFinder";
import { EvidencePanel } from "../components/network/EvidencePanel";
import { Drawer, DrawerContent, DrawerTitle } from "../components/ui/drawer";
import { useInvestigationState } from "../investigation/useInvestigationState";
import { setInvestigationTitle } from "../investigation/investigationMemory";
import { InvestigationContinues } from "../investigation/InvestigationContinues";

const DEFAULT_FOCUS = "central-intelligence-agency";

const ModeBtn = ({ active, onClick, icon: Icon, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
          className={`inline-flex items-center gap-1.5 border px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em] transition-colors ${active ? "border-[#D92D20] text-[#D92D20]" : "text-[#757065] hover:text-[#E4DFD3]"}`}
          style={{ borderColor: active ? "#D92D20" : "#3A3630" }}>
    <Icon size={11} /> {children}
  </button>
);

export default function Connections() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const pathFrom = params.get("from");
  const pathTo = params.get("to");
  const focus = slug || DEFAULT_FOCUS;

  const [mode, setMode] = useInvestigationState("cx-mode", pathFrom && pathTo ? "path" : "explore");   // explore | path
  const [view, setView] = useInvestigationState("cx-view", "wall"); // wall | structured | board
  const [graph, setGraph] = useState(null);
  const [status, setStatus] = useState("loading");
  const [trail, setTrail] = useInvestigationState("cx-trail", []);
  const [boardEdge, setBoardEdge] = useState(null);

  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);

  useEffect(() => {
    setInvestigationTitle(`Connections: ${focus.replace(/-/g, " ")}`);
  }, [focus]);

  useEffect(() => {
    let dead = false;
    setStatus("loading");
    fetchGraph(focus)
      .then((g) => { if (!dead) { setGraph(g); setStatus("ok"); } })
      .catch(() => { if (!dead) setStatus("error"); });
    return () => { dead = true; };
  }, [focus]);

  const goFocus = useCallback((next) => {
    setTrail((t) => (t[t.length - 1] === focus ? t : [...t, focus]).slice(-6));
    setBoardEdge(null);
    nav(`/connections/${next}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [focus, nav]);

  const doSearch = async (text) => {
    setQ(text);
    if (text.trim().length < 2) { setHits([]); return; }
    try { setHits(await searchEntities(text.trim())); } catch { setHits([]); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-10" data-testid="connections-page">
      <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#D92D20] flex items-center gap-2">
        <Network size={12} /> The Investigation Network
      </div>
      <h1 className="font-heading text-4xl sm:text-5xl text-[#E4DFD3] mt-2">Connections Explorer</h1>
      <p className="text-sm text-[#A39E93] mt-2 max-w-2xl leading-relaxed">
        Every line on this board is documented. The Archive remembers not just that two
        records are connected — but <em className="text-[#E4DFD3] not-italic">why</em>, and where the evidence lives.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <ModeBtn active={mode === "explore"} onClick={() => setMode("explore")}
                 icon={LayoutList} testid="mode-explore-btn">Explore</ModeBtn>
        <ModeBtn active={mode === "path"} onClick={() => setMode("path")}
                 icon={GitBranch} testid="mode-path-btn">Path Finder</ModeBtn>
        {mode === "explore" && (
          <>
            <span className="w-px h-5 bg-[#3A3630] mx-1" />
            <ModeBtn active={view === "wall"} onClick={() => setView("wall")}
                     icon={Users} testid="view-wall-btn">Portrait Wall</ModeBtn>
            <ModeBtn active={view === "structured"} onClick={() => setView("structured")}
                     icon={LayoutList} testid="view-structured-btn">Structured</ModeBtn>
            <ModeBtn active={view === "board"} onClick={() => setView("board")}
                     icon={Workflow} testid="view-board-btn">Board View</ModeBtn>
          </>
        )}
      </div>

      {mode === "explore" && (
        <>
          {/* Search-to-focus */}
          <div className="relative mt-5 max-w-md">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A554C]" />
            <input value={q} onChange={(e) => doSearch(e.target.value)}
                   placeholder="Focus on a person, organization or operation…"
                   data-testid="explorer-search-input"
                   className="w-full bg-[#0d0c0a] border pl-9 pr-8 py-2.5 text-[13px] text-[#E4DFD3] font-heading outline-none focus:border-[#D92D20] transition-colors"
                   style={{ borderColor: "#3A3630" }} />
            {q && (
              <button type="button" onClick={() => { setQ(""); setHits([]); }}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#5A554C] hover:text-[#E4DFD3]">
                <X size={13} />
              </button>
            )}
            {hits.length > 0 && (
              <div className="absolute z-30 mt-1 w-full border max-h-64 overflow-y-auto"
                   style={{ borderColor: "#3A3630", background: "#141210" }}
                   data-testid="explorer-search-results">
                {hits.map((h) => (
                  <button key={h.slug} type="button"
                          onClick={() => { setQ(""); setHits([]); goFocus(h.slug); }}
                          className="w-full text-left px-3 py-2 hover:bg-[#1d1a16] transition-colors flex items-center justify-between gap-2"
                          data-testid={`search-hit-${h.slug}`}>
                    <span className="text-[12px] text-[#E4DFD3] truncate">{h.name}</span>
                    <span className="font-mono text-[8px] uppercase tracking-[0.15em] shrink-0"
                          style={{ color: KIND_META[groupOf(h)]?.color }}>
                      {KIND_META[groupOf(h)]?.label} · {h.cameo_count}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Investigation trail */}
          {trail.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="investigation-trail">
              <span className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#5A554C]">trail</span>
              {trail.map((s, i) => (
                <React.Fragment key={`${s}-${i}`}>
                  <button type="button" onClick={() => nav(`/connections/${s}`)}
                          className="font-mono text-[9px] text-[#A68F5E] hover:text-[#D92D20] transition-colors underline underline-offset-2"
                          data-testid={`trail-crumb-${s}`}>
                    {s.replace(/-/g, " ")}
                  </button>
                  <span className="text-[#3A3630] text-[9px]">→</span>
                </React.Fragment>
              ))}
              <span className="font-mono text-[9px] text-[#E4DFD3]">{focus.replace(/-/g, " ")}</span>
            </div>
          )}

          <div className="mt-6">
            {status === "loading" && (
              <div className="py-20 text-center" data-testid="explorer-loading">
                <Loader2 size={18} className="animate-spin inline text-[#A68F5E]" />
              </div>
            )}
            {status === "error" && (
              <div className="py-16 text-center font-mono text-[11px] text-[#D92D20]"
                   data-testid="explorer-error">
                That file could not be pulled. Try searching for another entity.
              </div>
            )}
            {status === "ok" && graph && (
              view === "wall"
                ? <PortraitWall graph={graph} onFocus={goFocus} />
                : view === "structured"
                ? <StructuredView graph={graph} onFocus={goFocus} />
                : (
                  <>
                    <BoardView graph={graph} onFocus={goFocus} onSelectEdge={setBoardEdge} />
                    <Drawer open={!!boardEdge} onOpenChange={(o) => !o && setBoardEdge(null)}>
                      <DrawerContent className="bg-[#12110e] border-[#3A3630] max-h-[85vh] overflow-y-auto"
                                     data-testid="board-evidence-drawer">
                        <DrawerTitle className="sr-only">Connection evidence</DrawerTitle>
                        {boardEdge && (
                          <EvidencePanel a={boardEdge.a} b={boardEdge.b}
                                         onFollow={(s) => { setBoardEdge(null); goFocus(s); }} />
                        )}
                      </DrawerContent>
                    </Drawer>
                  </>
                )
            )}
          </div>
        </>
      )}

      {mode === "path" && (
        <div className="mt-6"><PathFinder initialFrom={pathFrom} initialTo={pathTo} /></div>
      )}

      {/* THE INVESTIGATION CONTINUES — permanent V2.0 navigation signature */}
      <InvestigationContinues path={`/connections/${focus}`} />
    </div>
  );
}
